
import React from 'react';
import { Helmet } from 'react-helmet';
import { logger } from '@/services/logger';

interface PageSEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonicalPath?: string;
  ogImage?: string;
  ogType?: 'website' | 'article' | 'business.business' | 'profile';
  noindex?: boolean;
  twitterCard?: 'summary' | 'summary_large_image' | 'app' | 'player';
  twitterSite?: string;
  publishedTime?: string;
  modifiedTime?: string;
  locale?: string;
  siteName?: string;
  structuredData?: Record<string, any>;
}

/**
 * Enhanced SEO component for main public pages
 */
export const PageSEO: React.FC<PageSEOProps> = ({
  title,
  description = "Finde, buche und verwalte Beauty-Termine in ganz Deutschland – schnell, einfach, mobil.",
  keywords = "Termine, Beauty, Spa, Massage, Friseur, Buchung, Studio, Kundenportal, Kalender",
  canonicalPath,
  ogImage = "https://www.chrona.it/og-image.jpg",
  ogType = "website",
  noindex = false,
  twitterCard = "summary_large_image",
  twitterSite = "@chrona_app",
  publishedTime,
  modifiedTime,
  locale = "de_DE",
  siteName = "CHRONA – Dein Termin. Deine Zeit.",
  structuredData
}) => {
  const siteTitle = siteName;
  const fullTitle = title ? `${title} | ${siteTitle}` : siteTitle;
  const canonical = canonicalPath ? 
    (canonicalPath.startsWith('http') ? canonicalPath : `https://www.chrona.it${canonicalPath.startsWith('/') ? '' : '/'}${canonicalPath}`) 
    : "https://www.chrona.it";

  // Log SEO data in development
  if (process.env.NODE_ENV === 'development') {
    logger.debug('PageSEO rendered', { 
      title: fullTitle, 
      description, 
      canonical,
      ogImage
    });
  }

  // Structured JSON-LD data
  const jsonLd = structuredData ? JSON.stringify(structuredData) : JSON.stringify({
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": siteName,
    "url": "https://www.chrona.it",
    "description": description,
    "potentialAction": {
      "@type": "SearchAction",
      "target": "https://www.chrona.it/search?query={search_term_string}",
      "query-input": "required name=search_term_string"
    }
  });

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <link rel="canonical" href={canonical} />
      
      {/* Robots Meta Tag */}
      <meta 
        name="robots" 
        content={noindex ? 'noindex, nofollow' : 'index, follow'} 
      />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage.startsWith('http') ? ogImage : `https://www.chrona.it${ogImage}`} />
      <meta property="og:site_name" content={siteName} />
      
      {/* Article specific tags */}
      {ogType === 'article' && publishedTime && (
        <meta property="article:published_time" content={publishedTime} />
      )}
      {ogType === 'article' && modifiedTime && (
        <meta property="article:modified_time" content={modifiedTime} />
      )}
      
      {/* Twitter */}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:site" content={twitterSite} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage.startsWith('http') ? ogImage : `https://www.chrona.it${ogImage}`} />
      
      {/* Mobile optimization */}
      <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=5.0" />
      <meta name="theme-color" content="#212121" />
      
      {/* Language */}
      <meta property="og:locale" content={locale} />
      <meta httpEquiv="content-language" content={locale.replace('_', '-')} />
      
      {/* JSON-LD structured data */}
      <script type="application/ld+json">{jsonLd}</script>
    </Helmet>
  );
};
